import { useCallback, useContext } from 'react';
import type { z, ZodType } from 'zod';

import { UserContext } from '~/components/providers/UserProvider';
import {
  getUseLocalStorageSettingsKey,
  useLocalStorageSettings,
} from '~/domains/common/hooks/useLocalStorageSettings';

const KEY_PREFIX = 'picksheet';

function useStoredPicksheet<T>({
  contestId,
  slateId,
  schema,
}: {
  contestId: string;
  slateId?: string;
  schema?: ZodType<T, z.ZodTypeDef, unknown>;
}) {
  const { user } = useContext(UserContext);

  const anonKey = [KEY_PREFIX, contestId, slateId].filter(Boolean).join('::');
  const authKey = [KEY_PREFIX, user?.id, contestId, slateId].filter(Boolean).join('::');
  const [anonValue, setAnonValue] = useLocalStorageSettings<T>(anonKey, null);
  const [authValue, setAuthValue] = useLocalStorageSettings<T>(authKey, null);

  const validate = useCallback(
    (value: unknown): T | null => {
      if (!schema) {
        return (value as T) ?? null;
      }
      const parsed = schema.safeParse(value);
      if (!parsed.success) {
        return null;
      }
      return parsed.data;
    },
    [schema]
  );

  const setValue = useCallback(
    (value: T) => {
      const validatedValue = validate(value);
      if (validatedValue === null) {
        return;
      }
      if (user) {
        setAuthValue(validatedValue);
        return;
      }
      setAnonValue(validatedValue);
    },
    [setAnonValue, setAuthValue, user, validate]
  );

  const getValue = useCallback(() => {
    const clearCorruptedStorage = (isAuth: boolean) => {
      const key = isAuth ? authKey : anonKey;
      localStorage.removeItem(getUseLocalStorageSettingsKey(key));
    };

    if (!user) {
      const parsedAnonValue = validate(anonValue);
      if (anonValue != null && parsedAnonValue === null) {
        clearCorruptedStorage(false);
      }
      return parsedAnonValue;
    }

    const parsedAuthValue = validate(authValue);
    if (authValue != null && parsedAuthValue === null) {
      clearCorruptedStorage(true);
    }

    const parsedAnonValue = validate(anonValue);
    if (anonValue != null && parsedAnonValue === null) {
      clearCorruptedStorage(false);
    }

    return parsedAuthValue || parsedAnonValue;
  }, [anonKey, anonValue, authKey, authValue, user, validate]);

  const clearAnonValue = useCallback(() => {
    localStorage.removeItem(getUseLocalStorageSettingsKey(anonKey));
  }, [anonKey]);

  const clearValue = useCallback(() => {
    try {
      if (!user) {
        clearAnonValue();
      } else {
        localStorage.removeItem(getUseLocalStorageSettingsKey(authKey));
      }
    } catch (e) {
      console.error(e);
    }
  }, [authKey, clearAnonValue, user]);

  return {
    setStoredPicks: setValue,
    getStoredPicks: getValue,
    clearStoredPicks: clearValue,
    clearStoredAnonPicks: clearAnonValue,
  };
}

export { useStoredPicksheet };
